import axios from 'axios'
import PropTypes from 'prop-types'
import find from 'lodash/find'
import mapValues from 'lodash/mapValues'
import { API_BASE_URL } from 'config'
import { kea } from 'kea'
import { put, call } from 'redux-saga/effects'
import { searchToObject, csObjectFromUnderscore, csObjectToUnderscore } from 'utils'
import { omit, pick } from 'lodash'

/** Contants */
const DEFAULT_VALUES = {
  hide_additional: false
}
const DEFAULT_SETTINGS = {
  iframed: false
}
const OFFER_TYPES_ENDPOINT = `${API_BASE_URL}/public/offer-type/`
const TRANSLATIONS_ENDPOINT = `${API_BASE_URL}/public/translations/`
const ME_ENDPOINT = `${API_BASE_URL}/private/user-profile/me/`
const SIMULATION_PROFILE_ENDPOINT = `${API_BASE_URL}/private/simulation-user-profile/`
const TRANSLATIONS_LOCAL_KEY = 'local'
const AUTH_TOKEN_KEY = `tokenauth`
const LOADING_COUNT = 3

const formatElectricityValues = ({ s, f, c, p, startDate, endDate }) => ({
  simples: s,
  svazio: f,
  cheias: c,
  ponta: p,
  startDate,
  endDate
})

/* eslint-disable camelcase */
const Logic = kea({
  path: () => ['scenes', 'app'],
  actions: () => ({

    getAppTranslations: () => ({}),
    getCurrentUser: () => ({}),
    setAppTranslations: (translations) => ({ translations }),

    setValue: (selected, item) => ({ values: { [item]: selected.value } }),
    setValues: (selected) => selected,
    setIsUserLoggedIn: (is) => ({ is }),

    setFinished: (value) => ({ value }),

    setOfferTypes: (offerTypes) => ({ offerTypes }),
    fetchOfferTypes: () => ({}),
    fetchError: (error) => ({ error }),
    setStarted: () => ({}),
    setSettings: (settings) => ({ settings }),

    fetchProfile: (id) => ({ id }),
    decreaseLoading: () => ({})

  }),

  reducers: ({ actions }) => ({

    values: [DEFAULT_VALUES, PropTypes.object, {
      [actions.setValue]: (state, payload) => ({...state, ...payload.values}),
      [actions.setValues]: (state, payload) => ({...state, ...payload})
    }],

    offerTypes: [null, PropTypes.any, {
      [actions.setOfferTypes]: (_, payload) => payload.offerTypes
    }],

    finished: [{}, PropTypes.bool, {
      [actions.setFinished]: (_, payload) => ({ ..._, ...payload.value })
    }],

    translations: [{}, PropTypes.object, {
      [actions.setAppTranslations]: (state, payload) => payload.translations
    }],

    userLoggedIn: [false, PropTypes.any, {
      [actions.setIsUserLoggedIn]: (_, payload) => payload.is
    }],

    started: [false, PropTypes.bool, {
      [actions.setStarted]: () => true
    }],

    settings: [DEFAULT_SETTINGS, PropTypes.object, {
      [actions.setSettings]: (state, payload) => ({ ...state, ...payload.settings })
    }],

    loading: [LOADING_COUNT, PropTypes.number, {
      [actions.decreaseLoading]: (state) => state - 1
    }]

  }),

  takeLatest: ({ actions, workers }) => ({
    [actions.fetchOfferTypes]: workers.fetchOfferTypes,
    [actions.getAppTranslations]: workers.getAppTranslations,
    [actions.getCurrentUser]: workers.getCurrentUser,
    [actions.fetchProfile]: workers.fetchProfile
  }),
  start: function * () {
    const {
      fetchOfferTypes,
      fetchProfile,
      setAppTranslations,
      getAppTranslations,
      getCurrentUser,
      setStarted,
      setSettings,
      decreaseLoading
    } = this.actions

    const appStarted = yield this.get('started')

    // Importing App makes the app do stuff again thats not needed
    // Using this we can prevent it
    if (appStarted) {
      return false
    }

    yield put(fetchOfferTypes())

    yield put(getCurrentUser())

    // Check if user has translations in localStorage
    // We need to do this because of performance issues
    const localTranslations = window.localStorage.getItem(TRANSLATIONS_LOCAL_KEY)

    if (localTranslations) {
      const parsedTranslations = JSON.parse(localTranslations)
      yield put(setAppTranslations(parsedTranslations))
    }

    // Check if there are any params in the first request
    const params = searchToObject(window.location.search)

    // Set iframe settings
    if (params.iframed) {
      document.body.classList.add('iframed')
      yield put(setSettings({ iframed: true }))
    }

    // Get profile if in settings
    if (params.profile) {
      yield put(fetchProfile(params.profile))
      yield put(setSettings({ isFromProfile: params.profile }))
    } else {
      yield put(decreaseLoading())
    }

    // We still want to update the translations anyway
    // But if user already visited we don't want unnecessary load time
    yield put(getAppTranslations())
    yield put(setStarted(true))
  },

  workers: {

    /**
     * Fetches offer type for the overall use in app
     */
    * fetchOfferTypes () {
      const { setOfferTypes, fetchError, decreaseLoading } = this.actions
      try {
        const response = yield call(axios.get, OFFER_TYPES_ENDPOINT, { limit: 1000 })
        const { results } = response.data

        if (results) {
          yield put(setOfferTypes(results))
          yield put(decreaseLoading())
        }
      } catch (error) {
        yield put(fetchError(error))
      }
    },

    /**
     * Gets app translations
     */
    * getAppTranslations () {
      const { setAppTranslations, setFatalError } = this.actions

      try {
        const request = yield call(axios.get, TRANSLATIONS_ENDPOINT)
        const results = request.data.results

        // Convert translations to a simpler object
        // This can be optimized #todo
        let translations = {}
        mapValues(results, (item) => {
          return mapValues(item.properties, (prop) => {
            translations[prop.key] = prop.value
          })
        })

        // Get local translations
        // We don't want the user to see stuff changing when hes using the app
        // Only update the storage on background and the next time he visits
        // He will have the translations updated
        const localTranslations = window.localStorage.getItem(TRANSLATIONS_LOCAL_KEY)
        if (!localTranslations) {
          yield put(setAppTranslations(translations))
        }

        // Set the new translations to localstorage
        window.localStorage.setItem(TRANSLATIONS_LOCAL_KEY, JSON.stringify(translations))
      } catch (error) {
        yield put(setFatalError())
      }
    },

    /**
     * Gets current user logged in
     */
    * getCurrentUser () {
      const { setIsUserLoggedIn, decreaseLoading } = this.actions

      try {
        const token = window.localStorage.getItem(AUTH_TOKEN_KEY)

        if (!token) {
          yield put(setIsUserLoggedIn(false))
          yield put(decreaseLoading())
          return
        }

        axios.defaults.headers.common['Authorization'] = `JWT ${token}`

        const me = yield call(axios.get, ME_ENDPOINT)
        yield put(setIsUserLoggedIn(me.data))
      } catch (error) {
        yield put(setIsUserLoggedIn(false))
        delete axios.defaults.headers.common['Authorization']
      }

      yield put(decreaseLoading())
    },

    /**
     * Fetchs profile and sets it as the current values
     * @param {*} action
     */
    * fetchProfile (action) {
      const { setValues, fetchError, decreaseLoading, setFinished } = this.actions
      const id = action.payload.id
      try {
        const request = yield call(axios.get, `${SIMULATION_PROFILE_ENDPOINT}${id}/`)

        // Convert Api input to camelCase and apply it to the redux state
        let values = csObjectFromUnderscore(request.data.simulation_history.api_input_data)

        if (values.gasConsumptionsRegistration) {
          values.gasConsumptionsRegistration = values.gasConsumptionsRegistration.map((item) =>
            csObjectFromUnderscore(item)
          )
        }

        if (values.electricityConsumptionsRegistration) {
          values.electricityConsumptionsRegistration = values.electricityConsumptionsRegistration.map((item) =>
            formatElectricityValues(csObjectFromUnderscore(item))
          )
        }

        const additional = pick(
          values.additionalData,
          [
            'adults',
            'children',
            'district',
            'elderly',
            'equipments'
          ]
        )

        const notCameled = csObjectToUnderscore(
          pick(values, [
            'invoicingType',
            'hasElectronicBilling',
            'hasJointBilling',
            'cicloHorario',
            'cicloTarifa',
            'electricityEntity',
            'gasEntity',
            'gasStage',
            'potenciaContratada'
          ])
        )

        values = { ...values, ...additional, ...notCameled }

        values = omit(values, [
          'invoicingType',
          'hasElectronicBilling',
          'hasJointBilling',
          'cicloHorario',
          'cicloTarifa',
          'electricityEntity',
          'gasEntity',
          'gasStage'
        ])

        // Remove simulationCode so we can generate a new one
        values = omit(values, ['simulationCode', 'equipment'])

        yield put(setValues(values))
        yield put(setFinished({
          profileFinished: true,
          habitationFinished: true,
          faturationFinished: true,
          consumeFinished: true
        }))
        yield put(decreaseLoading())
      } catch (error) {
        yield put(fetchError(error))
      }
    }

  },

  selectors: ({ selectors }) => ({
    /**
     * Selects the current offer type object based on id
     */
    offerType: [
      () => [selectors.values, selectors.offerTypes],
      ({ offerType }, offerTypes) => {
        return find(offerTypes, ({ id }) => id === offerType)
      },
      PropTypes.any
    ],

    iframed: [
      () => [selectors.settings],
      ({ iframed }) => iframed,
      PropTypes.any
    ]

  })
})

export default Logic



// WEBPACK FOOTER //
// ./src/containers/App/logic.js